/* import __COLOCATED_TEMPLATE__ from './edit-screen.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { restartableTask, task, timeout } from 'ember-concurrency';
import { CLASSIFICATION_SECTIONS } from 'sh-www/models/classification';
import { escapeRegExp, isUrl } from 'sh-www/utils/misc';
import { validateSchema, workSampleUrlValidator } from 'sh-www/utils/validators';
import { array, object, string } from 'yup';
import clsx from 'clsx';
import Validator from 'validator';

const CLASS_NAME = 'GLM-c-modals-overlays-lightbox-edit-screen';
const CLASSIFICATION_LIMIT = 4;
const THUMBNAIL_FETCH_DEBOUNCE_MS = 300;
const THUMBNAIL_LOAD_ERROR = "We were unable to load a video thumbnail. This may indicate that the video is not visible to the public. If possible, review the video's privacy setting.";
const THUMBNAIL_UPLOAD_ERROR = 'We were unable to upload your thumbnail. Please try a different image or refresh the page and try again.';
const VIDEO_LINK_BAD_ERROR = 'This link cannot be reached';

const SCHEMA = object({
  'title': string().required('You must provide a project title.'),
  'link': string()
    .required('You must provide a link.')
    .test(
      'link-requirement',
      'Link must be a valid URL',
      function(value) {
        const protocols = ['http', 'https'];
        return value ? Validator.isURL(value, { protocols }) : true;
      }
    ),
  'thumbnailSrc': string().required('A thumbnail must be provided.'),
  'description': string().required('You must provide a project description.')
    .max(500, 'Project description must be 500 characters or less.'),
  'services': array().required('Please select at least one service')
    .min(1, 'Please select at least one service'),
  'classifications': array().required('Please select at least one content tag')
    .min(1, 'Please select at least one content tag')
});

const categoryToClassificationSection = (category) => {
  return category.charAt(0).toUpperCase() + category.slice(1);
}

const hasErrors = (errors) => {
  return Object.values(errors).some((error) => error !== undefined);
}

const sortAndGroupByCategory = (options) => {
  const categoryOrder = Object.values(CLASSIFICATION_SECTIONS);

  return options.sort((a, b) => {
    // first sort by category
    const categoryComparison = categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
    if (categoryComparison !== 0) {
      return categoryComparison;
    }

    // then sort alphabetically
    return a.key.localeCompare(b.key);
  });
};

class GlmLightboxOverlayEditScreenComponent extends Component {
  @service errorTracker;
  @service('commands/image-commands') imageCommandService;
  @service media;
  @service('commands/oembed-commands') oembedCommandService;

  @tracked classifications = [];
  @tracked classificationsValue = '';
  @tracked description = this.args.selectedDigest.description;
  @tracked thumbnailSrc = this.args.selectedDigest.image?.transforms.embedThumbnail;
  @tracked customThumbnailImage = null;
  @tracked title = this.args.selectedDigest.title;
  @tracked link = this.args.selectedDigest.link;
  @tracked services = this.args.selectedDigest.services ?? [];

  #filteredClassificationOptionsCache = createCache(() => {
    const {
      classificationsValue,
      classificationOptions,
    } = this;

    if (!classificationsValue) {
      return classificationOptions;
    }

    const escapedInternalValue = escapeRegExp(classificationsValue);
    const pattern = new RegExp(`(\\b${escapedInternalValue})`, 'i');

    return classificationOptions.filter(({ key }) => {
      return pattern.test(key);
    });
  });

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get formDisabled() {
    return this.args.onSaveProjectdigestTask.isRunning;
  }

  get saveDisabled() {
    return this.formDisabled || this.isThumbnailReloading;
  }

  get classificationIdToModelMap() {
    return this.classifications.reduce((accum, classification) => {
      accum[classification.model] = classification;
      return accum;
    }, {});
  }

  get classificationOptions() {
    const { classificationIdToModelMap } = this;

    const options = (this.args.allClassifications ?? [])
      .filter((classification) => !classificationIdToModelMap[classification.id])
      .map((classification) => ({
        key: classification.label,
        model: classification.id,
        category: categoryToClassificationSection(classification.category),
      }));

    return sortAndGroupByCategory(options);
  }

  get filteredClassificationOptions() {
    return getValue(this.#filteredClassificationOptionsCache);
  }

  get isThumbnailReloading() {
    return this.thumbnailChange.isRunning || this.updateThumbnailPreview.isRunning;
  }

  get headerText() {
    return this.args.needsWorkSample
      ? 'Attach Work Sample'
      : 'Edit Work Sample';
  }

  get showThumbnailAndClassifications() {
    return Boolean(this.link || !this.args.needsWorkSample);
  }

  get classificationsCaption() {
    return this.classifications.length >= 4
      ? 'Maximum number of tags reached. To add a tag, first remove one.'
      : undefined;
  }

  @action
  initializeOverlay() {
    const classificationSelections = this.args.selectedDigest.classifications ?? [];
    this.classifications = classificationSelections.map(({
      category,
      id,
      label,
    }) => {
      return {
        key: label,
        model: id,
        category: categoryToClassificationSection(category),
      }
    });

    if (this.args.selectedDigest.badLink) {
      this.args.updateError('link', VIDEO_LINK_BAD_ERROR);
    }
  }

  @action
  handleCancel() {
    this.args.onCloseSecondaryScreen();
  }

  @action
  handleInput(name, value) {
    this.args.updateError(name, undefined);

    this[name] = value;

    if (name === 'link') {
      this.updateThumbnailPreview.perform(value);
    }
  }

  @action
  handleSubmit(e) {
    e.preventDefault();

    this.args.clearErrors();

    const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
    this.args.setErrors(validationErrors);

    // prevent save while errors exist
    if (hasErrors(validationErrors)) {
      return;
    }

    const {
      classifications,
      description,
      customThumbnailImage,
      title,
      link,
      services,
    } = this;
    const { selectedDigest } = this.args;

    try {
      this.args.onSaveProjectdigestTask.perform(selectedDigest, {
        classifications,
        description,
        image: customThumbnailImage,
        title,
        link,
        services,
      });
    } catch (error) {
      this.errorTracker.error('Save failed', this, error);
    }
  }

  @action
  handleClassificationBlur() {
    this.classificationsValue = '';
  }

  @action
  handleClassificationInput(value) {
    this.classificationsValue = value;
  }

  @action
  handleServiceAdded(addedService) {
    this.args.updateError('services', undefined);

    this.services = [ ...this.services, addedService ];
  }

  @action
  handleServiceRemoved(removedService) {
    this.args.updateError('services', undefined);

    this.services = this.services.filter((service) => service !== removedService);
  }

  @action
  handleClassificationAdded(addedClassification) {
    this.args.updateError('classifications', undefined);

    const currentClassifications = this.classifications;
    if (currentClassifications.length < CLASSIFICATION_LIMIT) {
      this.classifications = [ ...currentClassifications, addedClassification ];
    }

    // clear search
    this.classificationsValue = '';
  }

  @action
  handleClassificationRemoved(removedClassification) {
    this.args.updateError('classifications', undefined);

    this.classifications = this.classifications.filter((classification) => classification !== removedClassification);
  }

  @action
  thumbnailRemove() {
    this.thumbnailSrc = null;
  }

  /**
   *
   */
  @task
  *thumbnailChange(e) {
    this.args.updateError('thumbnailSrc', undefined);

    const [file] = e.target.files;

    if (!file) {
      return;
    }

    try {
      // upload draft to cloudinary
      const customThumbnailImage = yield this.imageCommandService.createDraftImage(file);
      this.args.onchange?.(customThumbnailImage);

      // show transformed image
      const thumbnailUrl = yield this.imageCommandService.cloudinaryThumbnailURLWithTransforms(
        customThumbnailImage.cloudVersion,
        customThumbnailImage.cloudId,
        'embed_thumbnail,f_auto,q_auto',
      );

      this.customThumbnailImage = customThumbnailImage;
      this.thumbnailSrc = thumbnailUrl;
    } catch (error) {
      this.errorTracker.error('Thumbnail upload failed', this, error);
      this.args.updateError('thumbnailSrc', THUMBNAIL_UPLOAD_ERROR);
    }
  }

  @restartableTask
  *updateThumbnailPreview(url) {
    this.args.updateError('thumbnailSrc', undefined);

    if (!isUrl(url) || workSampleUrlValidator(url)) {
      this.thumbnailSrc = '';
      return;
    }

    yield timeout(THUMBNAIL_FETCH_DEBOUNCE_MS);

    try {
      this.thumbnailSrc = yield this.oembedCommandService.getThumbnail(url, true);
    } catch (e) {
      this.args.updateError('thumbnailSrc', THUMBNAIL_LOAD_ERROR);
    }
  }
}

export default GlmLightboxOverlayEditScreenComponent;
